import React from "react";
import { cn } from "@/lib/utils";

/**
 * AvatarChip — small gradient-initial circle for assignee / reviewer metadata.
 * Adds warmth + scannability without the cost of a real avatar image.
 *
 * Usage:
 *   <AvatarChip name="Neel G" />
 *   <AvatarChip name="Priya Sharma" size={20} />
 */

const GRADIENTS = [
  "from-violet-400 to-purple-600",
  "from-pink-400 to-fuchsia-600",
  "from-emerald-400 to-green-600",
  "from-orange-400 to-orange-600",
  "from-sky-400 to-blue-600",
  "from-teal-400 to-cyan-600",
  "from-amber-400 to-yellow-600",
  "from-rose-400 to-red-500",
  "from-indigo-400 to-indigo-600",
  "from-lime-400 to-green-500",
];

function hashToIndex(str, mod) {
  if (!str) return 0;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % mod;
}

function getInitials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

export function AvatarChip({ name, size = 22, className, title }) {
  const initials = getInitials(name).toUpperCase();
  const gradient = GRADIENTS[hashToIndex(name || "", GRADIENTS.length)];
  const fontSize = Math.max(9, Math.floor(size * 0.42));
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full text-white font-semibold tracking-tight flex-shrink-0 bg-gradient-to-br",
        gradient,
        className
      )}
      style={{ width: size, height: size, fontSize }}
      title={title || name}
      aria-label={name}
    >
      {initials}
    </span>
  );
}

export default AvatarChip;
